import { useRef, useState } from 'react';
import { Create, useForm } from '@refinedev/antd';
import { HttpError } from '@refinedev/core';
import { Form, Input, Select, Alert } from 'antd';
import type {
  ToolkitConfig,
  ToolkitConfigInput,
  ToolkitType,
} from '@viq-admin/types';
import { MaskedSecretField, errorMessageWithFieldErrors, space } from '@viq-admin/ui';
import { ATS_LAYOUT_EXTRA, ATS_LAYOUT_PLACEHOLDER, ATS_PASSWORD_EXTRA, ATS_PASSWORD_WHITESPACE_RULE } from './atsCopy';
import { FPLEADS_BASE_URL_EXTRA, FPLEADS_DEFAULT_BASE_URL, FPLEADS_HTTPS_RULE, FPLEADS_SECRETS_EXTRA, FPLEADS_WHITESPACE_RULE } from './fpleadsCopy';
import { ATS_API_PASSWORD_RULE, ATS_API_SECRETS_EXTRA, ATS_API_SHOW_ID_EXTRA, ATS_API_SHOW_ID_RULE, ATS_API_USERNAME_RULE } from './atsApiCopy';
import { SHOWCARE_BASE_URL_EXTRA, SHOWCARE_DEFAULT_BASE_URL, SHOWCARE_HTTPS_RULE, SHOWCARE_LICENSE_KEY_EXTRA, SHOWCARE_LICENSE_KEY_RULE, SHOWCARE_BASE_URL_SHAPE_RULE } from './showcareCopy';
import { switchBaseUrl, type BaseUrlMemory } from './baseUrlDefaults';
import { RequireCan } from '../../components/RequireCan';
import { Can } from '../../ability/AbilityContext';

/**
 * Create a reusable toolkit config (global template). The per-`toolkitType`
 * config/secret field set is identical to the per-conference Toolkit tab
 * (`views/ConferenceDetail/Toolkit.tsx`); the only additions here are a leading
 * `name` field and `useForm` submission (Refine `toolkits` resource) instead of
 * the tab's imperative `putToolkit`.
 *
 * Body: `{ name, toolkitType, config, secrets? }` — `secrets` is omitted
 * entirely when every secret field is left blank. Secret inputs use
 * `MaskedSecretField` with `hasSecret={false}` (nothing stored yet on create)
 * and are wrapped in `<Can manage Toolkit>`. `manage Toolkit` gates the page.
 */

const TOOLKIT_OPTIONS: { label: string; value: ToolkitType }[] = [
  { label: 'attendee_list', value: 'attendee_list' },
  { label: 'plus_lead', value: 'plus_lead' },
  { label: 'expo_tools', value: 'expo_tools' },
  { label: 'badge_kit', value: 'badge_kit' },
  { label: 'conexsys (not implemented)', value: 'conexsys' },
  { label: 'xpress_lead_pro', value: 'xpress_lead_pro' },
  { label: 'maritz_swap', value: 'maritz_swap' },
  { label: 'edc', value: 'edc' },
  { label: 'exposmart', value: 'exposmart' },
  { label: 'ats', value: 'ats' },
  { label: 'fpleads', value: 'fpleads' },
  { label: 'showcare', value: 'showcare' },
  { label: 'ats_api', value: 'ats_api' },
];

interface FormShape {
  name: string;
  toolkitType: ToolkitType;
  config?: Record<string, string>;
  secrets?: Record<string, string>;
}

export function ToolkitsCreate() {
  const { formProps, saveButtonProps, onFinish } = useForm<
    ToolkitConfig,
    HttpError,
    ToolkitConfigInput
  >({
    resource: 'toolkits',
    action: 'create',
    errorNotification: (error) => ({
      key: 'toolkit-config-save-error',
      type: 'error',
      message: errorMessageWithFieldErrors(error, 'Failed to save toolkit config'),
    }),
  });
  const [toolkitType, setToolkitType] = useState<ToolkitType>('attendee_list');
  const baseUrlMemory = useRef<BaseUrlMemory>({});

  function handleFinish(values: FormShape) {
    const secretsFilled = Object.values(values.secrets ?? {}).filter(
      (v) => typeof v === 'string' && v.length > 0,
    ).length;
    const body: ToolkitConfigInput = {
      name: values.name,
      toolkitType: values.toolkitType,
      config: values.config ?? {},
    };
    if (secretsFilled > 0) {
      body.secrets = values.secrets;
    }
    return onFinish(body);
  }

  return (
    <RequireCan I="manage" a="Toolkit">
      <Create saveButtonProps={saveButtonProps}>
        <Form
          {...formProps}
          layout="vertical"
          initialValues={{
            toolkitType: 'attendee_list',
            config: {},
            secrets: {},
          }}
          onFinish={(values) => handleFinish(values as FormShape)}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
            extra="A recognizable label, e.g. Acme-BadgeKit-2026."
          >
            <Input maxLength={200} />
          </Form.Item>

          <Form.Item
            label="Toolkit type"
            name="toolkitType"
            rules={[{ required: true }]}
          >
            <Select
              options={TOOLKIT_OPTIONS}
              onChange={(v) => {
                const type = v as ToolkitType;
                const next = switchBaseUrl(baseUrlMemory.current, toolkitType, type, formProps.form?.getFieldValue(['config', 'base_url']));
                if (next) formProps.form?.setFieldValue(['config', 'base_url'], next);
                // Two types can share a secret field name (ats/ats_api `password`, exposmart/fpleads `api_key`), and antd keeps the store value, so a typed secret would reappear under the new vendor.
                formProps.form?.setFieldValue('secrets', {});
                setToolkitType(type);
              }}
            />
          </Form.Item>

          {toolkitType === 'attendee_list' && (
            <>
              <Form.Item
                label="API URL"
                name={['config', 'url']}
                rules={[{ required: true, type: 'url' }]}
              >
                <Input placeholder="https://api.example.com/attendee" />
              </Form.Item>
              <Form.Item
                label="Event UID"
                name={['config', 'evt_uid']}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Can I="manage" a="Toolkit">
                <Form.Item
                  label="Token"
                  name={['secrets', 'token']}
                  rules={[{ required: true, message: 'Token required' }]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
              </Can>
            </>
          )}

          {toolkitType === 'plus_lead' && (
            <Form.Item
              label="Filled URL"
              name={['config', 'filled_url']}
              rules={[{ required: true, type: 'url' }]}
              help="Include (barcode) as a placeholder, e.g. https://api/x?id=(barcode)"
            >
              <Input />
            </Form.Item>
          )}

          {toolkitType === 'expo_tools' && (
            <Form.Item
              label="Filled URL"
              name={['config', 'filled_url']}
              rules={[{ required: true, type: 'url' }]}
              help="Include (scanCode) as a placeholder"
            >
              <Input />
            </Form.Item>
          )}

          {toolkitType === 'xpress_lead_pro' && (
            <Form.Item
              label="Filled URL"
              name={['config', 'filled_url']}
              rules={[{ required: true, type: 'url' }]}
              help={
                'Paste the full XpressLeadPro filled_url including authkey, ' +
                'exid, eventcode, and placeholder tokens. Required tokens: ' +
                '(badgeNumber), (lastName), (utc_time), (current_time). ' +
                'Also supported: (firstName), (company), (showCode), (barcode).'
              }
            >
              <Input />
            </Form.Item>
          )}

          {toolkitType === 'maritz_swap' && (
            <Form.Item
              label="Filled URL"
              name={['config', 'filled_url']}
              rules={[{ required: true, type: 'url' }]}
              help={
                'Maritz SWAP LeadInfo URL with apikey, actcode, and ' +
                'operator badgeid baked in, plus (barcode) as a ' +
                'placeholder, e.g. https://developer.swap.mge360.com/' +
                'APIv1/LeadInfo?apikey=X&actcode=Y&badgeid=Z&barcode=(barcode)'
              }
            >
              <Input />
            </Form.Item>
          )}

          {toolkitType === 'edc' && (
            <Form.Item
              label="Filled URL"
              name={['config', 'filled_url']}
              rules={[{ required: true, type: 'url' }]}
              help={
                'EDC (retrievemyleads.com) LeadInfo URL with sid (show ' +
                'code) and key baked in, plus (aid) as a placeholder, ' +
                'e.g. https://retrievemyleads.com/api/meta/1.1/read/' +
                '?sid=SHOWCODE&key=KEY&aid=(aid)'
              }
            >
              <Input />
            </Form.Item>
          )}

          {toolkitType === 'badge_kit' && (
            <>
              <Form.Item
                label="Login URL"
                name={['config', 'login_url']}
                rules={[{ required: true, type: 'url' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Data extract URL"
                name={['config', 'data_extract_url']}
                rules={[{ required: true, type: 'url' }]}
                help="Include (referenceId), (eventId), (exhibitorId) placeholders"
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Event ID"
                name={['config', 'eventId']}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Exhibitor ID"
                name={['config', 'exhibitorId']}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Can I="manage" a="Toolkit">
                <Form.Item
                  label="API key"
                  name={['secrets', 'apikey']}
                  rules={[{ required: true, message: 'API key required' }]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
                <Form.Item
                  label="Secret"
                  name={['secrets', 'secret']}
                  rules={[{ required: true, message: 'Secret required' }]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
              </Can>
            </>
          )}

          {toolkitType === 'exposmart' && (
            <>
              <Form.Item
                label="URL"
                name={['config', 'url']}
                rules={[{ required: true, type: 'url' }]}
              >
                <Input placeholder="https://api.rcsreg.com/w/exposmart/at/v1.json" />
              </Form.Item>
              <Can I="manage" a="Toolkit">
                <Form.Item
                  label="API Key"
                  name={['secrets', 'api_key']}
                  rules={[{ required: true, message: 'API Key required' }]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
              </Can>
            </>
          )}

          {toolkitType === 'ats' && (
            <>
              <Form.Item
                label="Field layout"
                name={['config', 'field_layout']}
                rules={[{ required: true, message: 'Field layout required' }]}
                extra={ATS_LAYOUT_EXTRA}
              >
                <Input placeholder={ATS_LAYOUT_PLACEHOLDER} />
              </Form.Item>
              <Can I="manage" a="Toolkit">
                <Form.Item
                  label="Password"
                  name={['secrets', 'password']}
                  extra={ATS_PASSWORD_EXTRA}
                  rules={[
                    { required: true, message: 'Password required' },
                    ATS_PASSWORD_WHITESPACE_RULE,
                  ]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
              </Can>
            </>
          )}

          {toolkitType === 'fpleads' && (
            <>
              <Form.Item
                label="Base URL"
                name={['config', 'base_url']}
                initialValue={FPLEADS_DEFAULT_BASE_URL}
                validateFirst
                rules={[{ required: true, type: 'url' }, FPLEADS_HTTPS_RULE]}
                extra={FPLEADS_BASE_URL_EXTRA}
              >
                <Input />
              </Form.Item>
              <Can I="manage" a="Toolkit">
                <Form.Item
                  label="API Key"
                  name={['secrets', 'api_key']}
                  extra={FPLEADS_SECRETS_EXTRA}
                  rules={[{ required: true, message: 'API Key required' }, FPLEADS_WHITESPACE_RULE]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
                <Form.Item
                  label="User ID"
                  name={['secrets', 'user_id']}
                  rules={[{ required: true, message: 'User ID required' }, FPLEADS_WHITESPACE_RULE]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
              </Can>
            </>
          )}

          {toolkitType === 'showcare' && (
            <>
              <Form.Item
                label="Base URL"
                name={['config', 'base_url']}
                initialValue={SHOWCARE_DEFAULT_BASE_URL}
                validateFirst
                rules={[{ required: true, type: 'url' }, SHOWCARE_HTTPS_RULE, SHOWCARE_BASE_URL_SHAPE_RULE]}
                extra={SHOWCARE_BASE_URL_EXTRA}
              >
                <Input />
              </Form.Item>
              <Can I="manage" a="Toolkit">
                <Form.Item
                  label="License key"
                  name={['secrets', 'license_key']}
                  extra={SHOWCARE_LICENSE_KEY_EXTRA}
                  rules={[{ required: true, message: 'License key required' }, SHOWCARE_LICENSE_KEY_RULE]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
              </Can>
            </>
          )}

          {toolkitType === 'ats_api' && (
            <>
              <Form.Item
                label="Show ID"
                name={['config', 'show_id']}
                rules={[{ required: true, message: 'Show ID required' }, ATS_API_SHOW_ID_RULE]}
                extra={ATS_API_SHOW_ID_EXTRA}
              >
                <Input inputMode="numeric" />
              </Form.Item>
              <Can I="manage" a="Toolkit">
                <Form.Item
                  label="Username"
                  name={['secrets', 'username']}
                  extra={ATS_API_SECRETS_EXTRA}
                  rules={[{ required: true, message: 'Username required' }, ATS_API_USERNAME_RULE]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
                <Form.Item
                  label="Password"
                  name={['secrets', 'password']}
                  rules={[{ required: true, message: 'Password required' }, ATS_API_PASSWORD_RULE]}
                >
                  <MaskedSecretField hasSecret={false} />
                </Form.Item>
              </Can>
            </>
          )}

          {toolkitType === 'conexsys' && (
            <Alert
              type="warning"
              showIcon
              message="Conexsys integration not implemented yet — saving config is allowed, but the badge-scan endpoint returns 501 until the backend handler ships."
              style={{ marginBottom: space.lg }}
            />
          )}
        </Form>
      </Create>
    </RequireCan>
  );
}
